import { useState } from "react";
import { toast } from "sonner";
import { Send, Users } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { useFetch } from "../../lib/useFetch";
import { api, errMsg, fmtDate } from "../../lib/api";
import { Field } from "../../components/site/ProfileFields";
import { AdminH } from "./AdminLayout";

const TARGETS = [["all", "All registered users"], ["event", "Registered to a clinic/event"], ["class", "Sailors of a class"], ["interest", "Interested in a class"],
  ["unpaid", "Payment not completed"], ["single", "Single participant"], ["parents", "Parents of minors"]];

const S = (p) => <select {...p} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" />;

export default function AdminComms() {
  const { data: templates } = useFetch("/admin/templates");
  const { data: events } = useFetch("/admin/events");
  const { data: classes } = useFetch("/admin/classes");
  const { data: users } = useFetch("/admin/users");
  const { data: history, reload } = useFetch("/admin/messages");
  const [f, setF] = useState({ type: "all", value: "", subject: "", body: "", template: "" });
  const [recips, setRecips] = useState(null);

  const pickTpl = (key) => {
    const t = (templates || []).find((x) => x.key === key);
    setF({ ...f, template: key, subject: t?.subject || "", body: t?.body || "" });
  };
  const target = { type: f.type, value: f.value || null };
  const preview = async () => {
    try { setRecips((await api.post("/admin/messages/recipients", target)).data); } catch (e) { toast.error(errMsg(e)); }
  };
  const send = async () => {
    try {
      const { data } = await api.post("/admin/messages", { target, subject: f.subject, body: f.body, template: f.template || null });
      toast.success(`Email sent to ${data.recipients.length} recipients (simulated)`);
      setRecips(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const valueSel = {
    event: [(events || []).map((e) => [e.id, e.title])], unpaid: [(events || []).map((e) => [e.id, e.title]), "All events"],
    class: [(classes || []).map((c) => [c.name, c.name])], interest: [(classes || []).map((c) => [c.name, c.name])],
    single: [(users || []).map((u) => [u.id, `${u.first_name} ${u.last_name} (${u.email})`])],
  }[f.type];

  return (
    <div data-testid="admin-comms">
      <AdminH title="Communications" sub="Emails are SIMULATED in this prototype: they are stored in history and in each user's inbox." />
      <div className="grid gap-6 xl:grid-cols-5">
        <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-6 xl:col-span-3" data-testid="comms-composer">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Recipients"><S value={f.type} onChange={(e) => { setF({ ...f, type: e.target.value, value: "" }); setRecips(null); }} data-testid="comms-target-type">{TARGETS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</S></Field>
            {valueSel && <Field label="Select"><S value={f.value} onChange={(e) => { setF({ ...f, value: e.target.value }); setRecips(null); }} data-testid="comms-target-value"><option value="">{valueSel[1] || "—"}</option>{valueSel[0].map(([v, l]) => <option key={v} value={v}>{l}</option>)}</S></Field>}
          </div>
          <Field label="Template"><S value={f.template} onChange={(e) => pickTpl(e.target.value)} data-testid="comms-template-select"><option value="">Free text</option>{(templates || []).map((t) => <option key={t.id} value={t.key}>{t.name}</option>)}</S></Field>
          <Field label="Subject"><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="comms-subject" /></Field>
          <Field label="Message"><Textarea rows={9} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="comms-body" /></Field>
          <p className="text-xs text-slate-500">Placeholders {"{first_name}"} and {"{event_title}"} are filled automatically.</p>
          <div className="flex flex-wrap gap-3">
            <button onClick={preview} className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold" data-testid="comms-preview-recipients"><Users size={16} />Preview recipients</button>
            <button onClick={send} disabled={!f.subject || !f.body} className="btn-buoy !py-2 disabled:opacity-50" data-testid="comms-send"><Send size={16} />Send</button>
          </div>
          {recips && <div className="rounded-xl bg-slate-50 p-4 text-sm" data-testid="comms-recipients-list"><p className="mb-2 font-semibold">{recips.length} recipients</p>{recips.slice(0, 30).map((r) => <p key={r.user_id} className="text-slate-600">{r.name} &lt;{r.email}&gt;</p>)}</div>}
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 xl:col-span-2" data-testid="comms-history">
          <p className="mb-4 font-display text-lg font-bold text-[#0A2540]">Sent history</p>
          <div className="space-y-3">
            {(history || []).map((m) => (
              <details key={m.id} className="rounded-xl border border-slate-100 p-3" data-testid={`history-message-${m.id}`}>
                <summary className="cursor-pointer text-sm"><span className="font-semibold">{m.subject}</span><span className="block text-xs text-slate-500">{fmtDate(m.sent_at)} · {m.recipients.length} recipients · {m.target?.type}</span></summary>
                <p className="mt-2 whitespace-pre-line text-xs text-slate-600">{m.body}</p>
                <p className="mt-2 text-xs text-slate-500">{m.recipients.map((r) => r.email).join(", ")}</p>
              </details>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
